import type { ComponentType } from 'react'

/**
 * Extend this interface via module augmentation in each modal's own file.
 *
 * Example (in confirmation-modal/index.tsx):
 *   declare module 'components/organisms/modals/modal-registry' {
 *     interface ModalRegistry {
 *       'confirmation': ConfirmationModalManagedProps
 *     }
 *   }
 */
export interface ModalRegistry {}

export type ModalId = keyof ModalRegistry

// ---------------------------------------------------------------------------
// Component map — add one import + one entry per registered modal.
// The mapped type enforces that every key has a matching component type.
// ---------------------------------------------------------------------------
import { ManagedConfirmationModal } from './confirmation-modal'
import { ManagedAgeRestrictModal } from './age-restrict-modal'
import { ManagedLocationRestrictModal } from './location-restrict-modal'
import { ManagedSocialMediaInformationModal } from './social-media-information-modal'
import { ManagedProducerInformationModal } from './producer-information-modal'
import { ManagedGuestListFormatModal } from './guest-list-format-modal'
import { ManagedTermsAndConditionsModal } from './terms-and-conditions-modal'
import { ManagedPolicyPdfModal } from './policy-pdf-modal'
import { ManagedLogoutConfirmationModal } from './logout-confirmation-modal'
import { ManagedDeleteEventModal } from './delete-event-modal'
import { ManagedDeleteAccountModal } from './delete-account-modal'
import { ManagedLeavePageConfirmationModal } from './leave-page-confirmation-modal'
import { ManagedInviteLinkIneligibleModal } from './invite-link-ineligible-modal'
import { ManagedOfferUnavailableModal } from './offer-unavailable-modal'
import { ManagedEventRsvpTicketConfirmationModal } from './event-rsvp-ticket-confirmation-modal'
import { ManagedTicketPriceUpdatedModal } from './ticket-price-updated-modal'
import { ManagedRSVPConfirmationModal } from './rsvp-confirmation-modal'
import { ManagedEventCancelConfirmationModal } from './event-cancel-confirmation-modal'
import { ManagedLeaveLoyalinkRedirectModal } from './leave-loyalink-redirect-modal'
import { ManagedFilterChangeWarningModal } from './filter-change-warning-modal'
import { ManagedEventDateTimeWarningModal } from './event-datetime-warning-modal'
import { ManagedTimeFrameWarningModal } from './time-frame-warning-modal'
import { ManagedStripeSetupWarningModal } from './stripe-setup-warning-modal'
import { ManagedPaymentFailedModal } from './payment-failed-modal'
import { ManagedEligibilityErrorModal } from './eligibility-error-modal'
import { ManagedActivatePieModal } from './activate-pie-modal'
import { ManagedWaitlistSuccessModal } from './waitlist-success-modal'
import { ManagedRSVPSuccessModal } from './rsvp-success-modal'
import { ManagedContestWinnerModal } from './contest-winner-modal'
import { ManagedEventShareSuccessModal } from './event-share-success-modal'
import { ManagedShareSuccessModal } from './share-success-modal'
import { ManagedOfferSuccessModal } from './offer-success-modal'
import { ManagedApproveAllConfirmationModal } from './approve-all-confirmation-modal'
import { ManagedDeleteContactsModal } from './delete-contacts-modal'
import { ManagedShareOfferModal } from './share-offer-modal'
import { ManagedAttendeeDetailModal } from './attendee-detail-modal'
import { ManagedPayoutHistoryModal } from './payout-history-modal'
import { ManagedCheckedInStatisticsModal } from './checked-in-statistics-modal'
import { ManagedEventFullModal } from './event-full-modal'
import { ManagedContactHostModal } from './contact-host-modal'
import { ManagedSendContactMessageModal } from './send-contact-message-modal'
import { ManagedChangePasswordModal } from './change-password-modal'
import { ManagedSliceSelectCustomAmountModal } from './slice-select-custom-amount-modal'
import { ManagedReceivedTicketModal } from 'pages/Events/RSVP/components/received-ticket-modal'

export const MODAL_COMPONENTS: {
	[K in ModalId]: ComponentType<ModalRegistry[K] & { onClose: () => void }>
} = {
	confirmation: ManagedConfirmationModal,
	'age-restrict': ManagedAgeRestrictModal,
	'location-restrict': ManagedLocationRestrictModal,
	'social-media-information': ManagedSocialMediaInformationModal,
	'producer-information': ManagedProducerInformationModal,
	'guest-list-format': ManagedGuestListFormatModal,
	'terms-and-conditions': ManagedTermsAndConditionsModal,
	'policy-pdf': ManagedPolicyPdfModal,
	'logout-confirmation': ManagedLogoutConfirmationModal,
	'delete-event': ManagedDeleteEventModal,
	'delete-account': ManagedDeleteAccountModal,
	'leave-page-confirmation': ManagedLeavePageConfirmationModal,
	'invite-link-ineligible': ManagedInviteLinkIneligibleModal,
	'offer-unavailable': ManagedOfferUnavailableModal,
	'event-rsvp-ticket-confirmation': ManagedEventRsvpTicketConfirmationModal,
	'ticket-price-updated': ManagedTicketPriceUpdatedModal,
	'rsvp-confirmation': ManagedRSVPConfirmationModal,
	'event-cancel-confirmation': ManagedEventCancelConfirmationModal,
	'leave-loyalink-redirect': ManagedLeaveLoyalinkRedirectModal,
	'filter-change-warning': ManagedFilterChangeWarningModal,
	'event-datetime-warning': ManagedEventDateTimeWarningModal,
	'time-frame-warning': ManagedTimeFrameWarningModal,
	'stripe-setup-warning': ManagedStripeSetupWarningModal,
	'payment-failed': ManagedPaymentFailedModal,
	'eligibility-error': ManagedEligibilityErrorModal,
	'activate-pie': ManagedActivatePieModal,
	'waitlist-success': ManagedWaitlistSuccessModal,
	'rsvp-success': ManagedRSVPSuccessModal,
	'contest-winner': ManagedContestWinnerModal,
	'event-share-success': ManagedEventShareSuccessModal,
	'share-success': ManagedShareSuccessModal,
	'offer-success': ManagedOfferSuccessModal,
	'approve-all-confirmation': ManagedApproveAllConfirmationModal,
	'delete-contacts': ManagedDeleteContactsModal,
	'share-offer': ManagedShareOfferModal,
	'attendee-detail': ManagedAttendeeDetailModal,
	'payout-history': ManagedPayoutHistoryModal,
	'checked-in-statistics': ManagedCheckedInStatisticsModal,
	'event-full': ManagedEventFullModal,
	'contact-host': ManagedContactHostModal,
	'send-contact-message': ManagedSendContactMessageModal,
	'change-password': ManagedChangePasswordModal,
	'slice-select-custom-amount': ManagedSliceSelectCustomAmountModal,
	'received-ticket': ManagedReceivedTicketModal,
}
